import { Box, Button, Paper, Snackbar, Stack, Typography } from '@mui/material';
import { useAppUpdate } from '../hooks/useAppUpdate';
import { colors, mono } from '../themes/theme';

/**
 * Offers a new version rather than taking it.
 *
 * An automatic reload would land whenever a deploy did, which could be while
 * somebody is halfway through placing a bet. The running version keeps working
 * until they tap.
 *
 * Anchored above the tab bar rather than at the top: on an installed phone the
 * top of the screen is the notch, and a card up there reads as cut off.
 */
export function UpdatePrompt() {
    const update = useAppUpdate();

    return (
        <Snackbar
            open={update.available}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{
                // Clear of the tab bar and the home indicator beneath it.
                bottom: 'calc(84px + env(safe-area-inset-bottom)) !important',
                left: 16,
                right: 16,
                justifyContent: 'center',
            }}
        >
            <Paper
                elevation={8}
                sx={{
                    // MUI's default snackbar surface inverts to near-white in a
                    // dark theme, which is why this carries its own.
                    bgcolor: colors.raised,
                    borderColor: colors.safety,
                    p: 1.75,
                    width: '100%',
                    maxWidth: 380,
                    mx: 'auto',
                }}
            >
                <Stack spacing={1.5} alignItems="center" textAlign="center">
                    <Box>
                        <Typography
                            variant="overline"
                            sx={{ color: colors.safety, display: 'block' }}
                        >
                            Update ready
                        </Typography>
                        <Typography sx={{ color: colors.ink, fontWeight: 600 }}>
                            A newer version of Pony Up is waiting
                        </Typography>
                        <Typography
                            sx={{ fontFamily: mono, fontSize: '0.68rem', color: colors.muted }}
                        >
                            {update.newVersion
                                ? `${update.commit} → ${update.newVersion}`
                                : 'Reloading keeps you signed in'}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={update.apply}
                        >
                            Reload now
                        </Button>
                        <Button color="inherit" onClick={update.dismiss} sx={{ flexShrink: 0 }}>
                            Later
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        </Snackbar>
    );
}
